import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useCachedImage = (filePath: string | null) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!filePath) {
      setIsLoading(false);
      return;
    }

    const loadImage = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check if we have the image URL cached in localStorage
        const cachedUrl = localStorage.getItem(`img_${filePath}`);
        const cachedTimestamp = localStorage.getItem(`img_timestamp_${filePath}`);
        
        // Cache is valid for 24 hours
        const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        const isExpired = cachedTimestamp && (Date.now() - parseInt(cachedTimestamp)) > CACHE_DURATION;

        if (cachedUrl && !isExpired) {
          setImageUrl(cachedUrl);
          setIsLoading(false);
          return;
        }

        // If no valid cache, fetch new signed URL
        const { data: { signedUrl }, error } = await supabase.storage
          .from('food_images')
          .createSignedUrl(filePath, 86400); // 24 hours expiry

        if (error) throw error;
        if (!signedUrl) throw new Error('Failed to get signed URL');

        // Cache the new URL and timestamp
        localStorage.setItem(`img_${filePath}`, signedUrl);
        localStorage.setItem(`img_timestamp_${filePath}`, Date.now().toString());

        setImageUrl(signedUrl);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load image'));
      } finally {
        setIsLoading(false);
      }
    };

    loadImage();
  }, [filePath]);

  return { imageUrl, isLoading, error };
}; 